/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Typography,
} from "@insite/mobius/index";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadProductPriceBreaks from "@monsma/App/Handlers/Data/Products/LoadProductPriceBreaks";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    userName: state.context.session.userName,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    loadProductPriceBreaks,
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductPriceBreaksStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
}

export const productPriceBreaksStyles: ProductPriceBreaksStyles = {
    wrapper: {
        css: css`
            margin-bottom: 40px;

            @media print {
                page-break-inside: avoid;
                break-inside: avoid;
            }
        `,
    },
    title: {
        variant: "h3",
        as: "h2",
        color: "secondary.main",
        css: css`
            margin-bottom: 20px;
        `,
    },
};

const styles = productPriceBreaksStyles;

const ProductPriceBreaksWidget: React.FC<Props> = ({
    product,
    userName,
    currentUserIsGuest,
    loadProductPriceBreaks,
    showPricing,
}) => {
    const authorized = userName && !currentUserIsGuest;
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";

    if (!authorized && !isCmsEditing) {
        return null;
    }

    const [productPriceBreaks, setProductPriceBreaks] = useState([]);

    useEffect(() => {
        if (!showPricing) {
            return;
        }
        loadProductPriceBreaks({
            id: product?.id,
            onSuccess: (result: any) => {
                setProductPriceBreaks(result.productBreakPricingCollection);
            },
            onError: (error: any) => {},
        });
    }, [product?.id, showPricing]);

    if (!productPriceBreaks || !productPriceBreaks.length || !showPricing) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Pricing")}</Typography>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader>{translate("Price / Disc Type")}</DataTableHeader>
                    <DataTableHeader>{translate("Price")}</DataTableHeader>
                    <DataTableHeader>{translate("Price UOM")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {productPriceBreaks.map((productPriceBreak: any, index) => {
                        return (
                            <DataTableRow key={`productPriceBreak_${index}`}>
                                <DataTableCell>{productPriceBreak.priceType || "--"}</DataTableCell>
                                <DataTableCell>{productPriceBreak.costPerItem || "--"}</DataTableCell>
                                <DataTableCell>{productPriceBreak.priceUom || "--"}</DataTableCell>
                            </DataTableRow>
                        );
                    })}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductPriceBreaksWidget)),
    definition: {
        displayName: "Product Price Breaks",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
